import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BASE_URL from "../../../config/config";
// import "../../../css/admin.module.css";

// وضعیت به رنگ
function getStatusColor(status) {
  switch (status) {
    case "rejected": return "error";
    case "approved": return "success";
    case "pending": return "warning";
    case "spam": return "danger";
    case "deleted": return "secondary";
    default: return "secondary";
  }
}

function translateStatus(status) {
  switch (status) {
    case "rejected": return "رد شده";
    case "approved": return "تایید شده";
    case "pending": return "در انتظار بررسی";
    case "spam": return "اسپم";
    case "deleted": return "حذف شده";
    default: return status;
  }
}

const contentTypeLabels = {
  course: "دوره آموزشی",
  post: "مقاله",
  product: "محصول",
};

const CommentListManagement = () => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [message, setMessage] = useState("");

  // صفحه‌بندی
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  // جستجو
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);

  // مرتب‌سازی
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  const [csrfToken, setCsrfToken] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const res = await fetch(`${BASE_URL}/csrf-token`, { credentials: "include" });
        const data = await res.json();
        setCsrfToken(data.csrfToken);
      } catch {
        setError("دریافت توکن امنیتی با خطا مواجه شد");
      }
    };
    fetchToken();
  }, []);

  useEffect(() => {
    if (csrfToken) fetchComments();
  }, [csrfToken, currentPage, searchQuery, sortField, sortDirection]);

  // دریافت نظرات از سرور
  const fetchComments = async () => {
    setLoading(true);
    try {
      let url = `${BASE_URL}/admin/comments?page=${currentPage}&limit=${itemsPerPage}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      url += `&sortField=${sortField}&sortDirection=${sortDirection}`;

      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "CSRF-Token": csrfToken,
        },
        credentials: "include",
      });

      if (!response.ok) throw new Error("خطا در دریافت نظرات");

      const data = await response.json();
      // اطمینان حاصل کنید که `data.data.comments` همان ساختار درختی را دارد
      setComments(data.data.comments || []);
      setTotalPages(data.data.pagination?.totalPages || 1);
      setLoading(false);
      setSearching(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      setSearching(false);
    }
  };

  // ثبت پاسخ به عنوان نظر جدید
  const handleSubmitReply = async (parentId, contentId, contentType) => {
    if (!replyText.trim()) {
      setMessage("لطفا متن پاسخ را وارد کنید");
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/admin/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify({
          comment_text: replyText,
          parent_id: parentId,
          content_id: contentId,
          content_type: contentType,
          status: 'approved' // پاسخ مدیر مستقیم تایید می‌شود
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // بهترین راه برای به‌روزرسانی لیست بعد از ارسال پاسخ، فراخوانی مجدد fetchComments است
        fetchComments();
        setReplyingTo(null);
        setReplyText('');
        setMessage("پاسخ شما با موفقیت ثبت شد");
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage(data.message || "خطا در ثبت پاسخ");
      }
    } catch (err) {
      setMessage("خطا در ارتباط با سرور");
    }
  };

  // تغییر وضعیت نظر (بازگشتی برای به‌روزرسانی در درخت)
  const handleStatusChange = async (commentId, newStatus) => {
    try {
      const res = await fetch(`${BASE_URL}/admin/comments/${commentId}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify({ status: newStatus }),
      });

      const data = await res.json();

      if (res.ok) {
        const updateStatusInTree = (commentsArr) => {
          return commentsArr.map(comment => {
            if (comment.comment_id === commentId) {
              return { ...comment, status: newStatus };
            }
            if (comment.replies && comment.replies.length > 0) {
              // به صورت بازگشتی در پاسخ‌ها نیز جستجو کن
              return { ...comment, replies: updateStatusInTree(comment.replies) };
            }
            return comment;
          });
        };
        setComments(prev => updateStatusInTree(prev));
        setMessage(`وضعیت نظر به ${translateStatus(newStatus)} تغییر یافت`);
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage(data.message || "خطا در تغییر وضعیت نظر");
      }
    } catch (err) {
      setMessage("خطا در ارتباط با سرور");
    }
  };

  // حذف نظر (بازگشتی برای حذف از درخت)
  const handleDeleteComment = async (commentId) => {
    if (window.confirm("آیا از حذف این نظر مطمئن هستید؟")) {
      try {
        const res = await fetch(`${BASE_URL}/admin/comments/${commentId}`, {
          method: "DELETE",
          headers: {
            "CSRF-Token": csrfToken,
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        const data = await res.json();

        if (res.ok) {
          const deleteCommentFromTree = (commentsArr) => {
            return commentsArr.filter(comment => {
              if (comment.comment_id === commentId) {
                return false; // این نظر را حذف کن
              }
              if (comment.replies && comment.replies.length > 0) {
                // به صورت بازگشتی در پاسخ‌ها نیز حذف کن
                comment.replies = deleteCommentFromTree(comment.replies);
              }
              return true;
            });
          };
          setComments(prev => deleteCommentFromTree(prev));
          setMessage(data.message || "نظر با موفقیت حذف شد");
          setTimeout(() => setMessage(""), 3000);
        } else {
          setMessage(data.message || "خطا در حذف نظر");
        }
      } catch {
        setMessage("خطا در برقراری ارتباط با سرور");
      }
    }
  };

  // تابع بازگشتی برای نمایش نظرات و پاسخ‌ها
  // این تابع حالا لیستی از نظرات را می‌گیرد و هر یک از آن‌ها را رندر می‌کند.
  // سپس اگر پاسخی داشتند، خودش را برای آن پاسخ‌ها دوباره فراخوانی می‌کند.
  const renderComments = (commentList, level = 0) => {
    if (!commentList || commentList.length === 0) {
      return null; // اگر لیستی برای نمایش نیست، چیزی برنگردان
    }

    return commentList.map(comment => (
      <React.Fragment key={comment.comment_id}>
        <tr className={level > 0 ? 'reply-row' : ''}>
          <td>{comment.comment_id}</td>
          <td>
            {comment.user?.id ? (
              <a href={`/admin/users/edit/${comment.user.id}`}>
                {comment.user.name || comment.user.username || "کاربر"}
              </a>
            ) : (
              comment.user?.name || "مهمان"
            )}
          </td>
          <td>
            {contentTypeLabels[comment.content_type] || comment.content_type}
            <br />
            <small>ID: {comment.content_id}</small>
          </td>
          {/* اینجا اصلاح برای تورفتگی متن نظر اعمال می‌شود */}
          <td className="comment-text-cell" style={{ paddingRight: `${level * 30 + 12}px` }}> {/* 12px padding پیش‌فرض */}
            {level > 0 && <span className="reply-indicator">↳ </span>}
            {comment.comment_text}
          </td>
          <td>
            <select
              value={comment.status}
              onChange={(e) => handleStatusChange(comment.comment_id, e.target.value)}
              className={`status-select ${getStatusColor(comment.status)}`}
            >
              <option value="pending">در انتظار بررسی</option>
              <option value="approved">تایید شده</option>
              <option value="rejected">رد شده</option>
              <option value="spam">اسپم</option>
              <option value="deleted">حذف شده</option>
            </select>
          </td>
          <td>
            {new Date(comment.created_at).toLocaleDateString("fa-IR")}
          </td>
          <td>
            {comment.updated_at && comment.updated_at !== comment.created_at
              ? new Date(comment.updated_at).toLocaleDateString("fa-IR")
              : "-"}
          </td>
          <td>
            {/* اینجا اصلاح برای دکمه‌های عملیات کنار هم اعمال می‌شود */}
            <div className="comment-actions">
              <button
                className="reply-comment-btn"
                onClick={() => setReplyingTo(replyingTo === comment.comment_id ? null : comment.comment_id)}
                title="پاسخ"
              >
                <i className="fas fa-reply"></i>
              </button>
              <button
                className="edit-comment-btn"
                onClick={() => navigate(`/admin/comments/edit/${comment.comment_id}`, {
                  state: { commentData: comment }
                })}
                title="ویرایش"
              >
                <i className="fas fa-edit"></i>
              </button>
              <button
                className="delete-comment-btn"
                onClick={() => handleDeleteComment(comment.comment_id)}
                title="حذف"
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>
          </td>
        </tr>

        {/* فرم پاسخ */}
        {replyingTo === comment.comment_id && (
          <tr className="reply-form-row">
            <td colSpan="8">
              {/* اینجا اصلاح برای تورفتگی فرم پاسخ اعمال می‌شود */}
              <div className="reply-form" style={{ paddingRight: `${level * 30 + 15}px` }}> {/* 15px padding پیش‌فرض */}
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="پاسخ خود را بنویسید..."
                  rows="3"
                />
                <div className="reply-actions"> {/* اینها در CSS با flex-end هستند */}
                  <button
                    onClick={() => handleSubmitReply(comment.comment_id, comment.content_id, comment.content_type)}
                    className="submit-reply-btn"
                  >
                    ثبت پاسخ
                  </button>
                  <button
                    onClick={() => {
                      setReplyingTo(null);
                      setReplyText('');
                    }}
                    className="cancel-reply-btn"
                  >
                    انصراف
                  </button>
                </div>
              </div>
            </td>
          </tr>
        )}

        {comment.replies && comment.replies.length > 0 && (
          renderComments(comment.replies, level + 1)
        )}
      </React.Fragment>
    ));  
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setCurrentPage(1);
    setSearching(true);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  if (loading) return <div className="loading">در حال بارگذاری نظرات...</div>;

  return (
    <div className="comment-list-container">
      <div className="comment-list-header">
        <h2>مدیریت نظرات</h2>
      </div>

      <div className="comment-search-box">
        <form onSubmit={handleSearch}>
          <input
            type="text"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="جستجو بر اساس نام کاربر یا متن نظر..."
          />
          <button type="submit">جستجو</button>
          {searchQuery && (
            <button type="button" onClick={clearSearch}>پاک کردن</button>
          )}
        </form>
      </div>

      {message && (
        <div className={`alert ${message.includes("موفق") ? "alert-success" : "alert-danger"}`}>
          {message}
        </div>
      )}

      {error && (
        <div className="error">{error}</div>
      )}

      {comments.length === 0 ? (
        <div className="no-comments-found">نظری یافت نشد.</div>
      ) : (
        <div className="table-responsive">
          <table className="comments-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("comment_id")}>#</th>
                <th onClick={() => handleSort("user_name")}>کاربر</th>
                <th onClick={() => handleSort("content_type")}>نوع محتوا</th>
                <th>متن نظر</th>
                <th onClick={() => handleSort("status")}>وضعیت</th>
                <th onClick={() => handleSort("created_at")}>تاریخ ثبت</th>
                <th onClick={() => handleSort("updated_at")}>آخرین ویرایش</th>
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {/* فراخوانی اولیه تابع renderComments با لیست اصلی نظرات */}
              {renderComments(comments)}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            قبلی
          </button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i + 1}
              onClick={() => setCurrentPage(i + 1)}
              className={currentPage === i + 1 ? "active" : ""}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            بعدی
          </button>
        </div>
      )}
    </div>
  );
};

export default CommentListManagement;